import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { useAuth } from "./AuthContext";

const KINDS = [
  ["", "All"],
  ["player", "Players"],
  ["project", "County projects"],
  ["correction", "Corrections"],
  ["diaspora", "Diaspora register"],
  ["referral", "Grounds and stadiums"],
];

const DECISIONS = [
  ["in_review", "In review"],
  ["verified", "Verified"],
  ["published", "Published"],
  ["needs_info", "Needs info"],
  ["rejected", "Rejected"],
];

const badge = "mono border px-2 py-1 text-[0.55rem] uppercase tracking-[0.14em]";

/** Human review queue. The AI panel is advisory: a person sets every status. */
export default function Intake() {
  const [items, setItems] = useState([]);
  const [kind, setKind] = useState("");
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(null);
  const { user } = useAuth();

  const load = useCallback(async (k) => {
    const [list, s] = await Promise.all([
      api.get(`/admin/intake${k ? `?kind=${k}` : ""}`),
      api.get("/admin/intake/stats"),
    ]);
    setItems(list.data);
    setStats(s.data);
  }, []);

  useEffect(() => {
    load(kind).catch((e) => toast.error(errText(e)));
  }, [kind, load]);

  const decide = async (id, status) => {
    try {
      const { data } = await api.post(`/admin/intake/${id}/status`, { status, note: "" });
      setItems((prev) => prev.map((i) => (i.id === id ? data : i)));
      toast.success(`Marked ${status.replace("_", " ")}`);
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const reanalyse = async (id) => {
    try {
      await api.post(`/admin/intake/${id}/analyse`);
      toast.success("AI pass queued — refresh in a moment");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const publishCorrection = async (id) => {
    try {
      await api.post(`/admin/corrections/from-intake/${id}`);
      toast.success("Published to the corrections log");
      load(kind).catch(() => {});
    } catch (e) {
      toast.error(errText(e));
    }
  };

  const remove = async (id) => {    try {
      await api.delete(`/admin/intake/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(errText(e));
    }
  };

  return (
    <div data-testid="admin-intake-page">
      <p className="overline">Community intake</p>
      <h1 className="display-2 mt-4">Submissions queue</h1>

      {stats ? (
        <dl className="mono mt-8 grid gap-3 sm:grid-cols-4" data-testid="intake-stats">
          {[
            ["Total", stats.total],
            ["New", stats.by_status.new],
            ["Duplicate flags", stats.flagged_duplicate],
            ["Awaiting AI", stats.awaiting_ai],
          ].map(([k, v]) => (
            <div key={k} className="border border-zinc-900 bg-ink-surface p-4">
              <dt className="text-[0.55rem] uppercase tracking-[0.2em] text-zinc-600">{k}</dt>
              <dd className="mt-2 font-display text-2xl text-white">{v}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      <div className="no-scrollbar mt-8 flex gap-1 overflow-x-auto">
        {KINDS.map(([k, l]) => (
          <button
            key={k || "all"}
            onClick={() => setKind(k)}
            data-testid={`intake-tab-${k || "all"}`}
            className={`shrink-0 px-5 py-2.5 text-[0.68rem] font-bold uppercase tracking-[0.16em] transition-colors ${
              kind === k ? "bg-maasai text-white" : "text-zinc-500 hover:text-white"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-3" data-testid="intake-list">
        {items.length === 0 ? (
          <p className="body-copy">Nothing here yet.</p>
        ) : (
          items.map((s) => (
            <div key={s.id} className="border border-zinc-900 bg-ink-surface p-5" data-testid={`intake-${s.reference}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="mono text-[0.58rem] uppercase tracking-[0.18em] text-ochre">{s.reference} · {s.kind}</p>
                  <p className="mt-1.5 font-display text-base uppercase tracking-wide text-white">{s.title}</p>
                  <p className="mt-1 text-xs text-zinc-500">
                    {s.submitter?.name} · {s.submitter?.email} · {s.submitter?.role}
                    {s.private ? " · private" : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`${badge} border-zinc-700 text-zinc-300`} data-testid={`intake-status-${s.reference}`}>
                    {s.status.replace("_", " ")}
                  </span>
                  <button onClick={() => reanalyse(s.id)} aria-label="Re-run AI pass" data-testid={`intake-reanalyse-${s.reference}`} className="border border-zinc-800 p-2 text-zinc-500 hover:border-ochre hover:text-ochre">
                    <RefreshCw size={13} />
                  </button>
                  {user?.role === "super_admin" && (
                    <button onClick={() => remove(s.id)} aria-label="Delete submission" data-testid={`intake-delete-${s.reference}`} className="border border-zinc-800 p-2 text-zinc-500 hover:border-maasai hover:text-maasai">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>

              {s.ai_status === "done" && s.ai ? (
                <div className="mt-5 border-l-2 border-ochre bg-black p-4" data-testid={`intake-ai-${s.reference}`}>
                  <p className="mono text-[0.55rem] uppercase tracking-[0.18em] text-zinc-500">
                    AI triage (advisory) · {s.ai.model} · confidence {s.ai.confidence} · quality {s.ai.quality_score}/100
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-zinc-300">{s.ai.summary}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {s.ai.category ? <span className={`${badge} border-zinc-700 text-zinc-300`}>{s.ai.category}</span> : null}
                    <span className={`${badge} border-zinc-700 text-zinc-300`}>{s.ai.recommended_action?.replace(/_/g, " ")}</span>
                    {s.ai.duplicate ? (
                      <span className={`${badge} border-maasai text-maasai`} data-testid={`intake-duplicate-${s.reference}`}>
                        Possible duplicate: {s.ai.duplicate_of || "unknown"}
                      </span>
                    ) : null}
                  </div>
                  {s.ai.facts?.length ? (
                    <dl className="mono mt-4 grid gap-3 border-t border-zinc-900 pt-4 text-xs sm:grid-cols-3">
                      {s.ai.facts.map((f, i) => (
                        <div key={`${f.label}-${i}`}>
                          <dt className="text-[0.55rem] uppercase tracking-[0.18em] text-zinc-600">{f.label}</dt>
                          <dd className="mt-1 text-zinc-300">{f.value}</dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}
                  {s.ai.missing_information?.length ? (
                    <p className="mt-4 text-xs text-zinc-500">Missing: {s.ai.missing_information.join("; ")}</p>
                  ) : null}
                </div>
              ) : (
                <p className="mono mt-5 text-[0.55rem] uppercase tracking-[0.18em] text-zinc-600">
                  AI triage {s.ai_status}
                </p>
              )}

              <button
                onClick={() => setOpen(open === s.id ? null : s.id)}
                data-testid={`intake-toggle-${s.reference}`}
                className="mono mt-5 text-[0.58rem] uppercase tracking-[0.16em] text-zinc-500 hover:text-ochre"
              >
                {open === s.id ? "Hide submission" : "Show submission"}
              </button>
              {open === s.id ? (
                <dl className="mono mt-4 grid gap-3 border-t border-zinc-900 pt-4 text-xs sm:grid-cols-3" data-testid={`intake-payload-${s.reference}`}>
                  {Object.entries(s.payload || {})
                    .filter(([, v]) => v && String(v).length)
                    .map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-[0.55rem] uppercase tracking-[0.18em] text-zinc-600">{k.replace(/_/g, " ")}</dt>
                        <dd className="mt-1 whitespace-pre-line break-words text-zinc-300">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
                      </div>
                    ))}
                </dl>
              ) : null}

              <div className="mt-5 flex flex-wrap gap-2 border-t border-zinc-900 pt-4">
                {s.kind === "correction" && s.status !== "published" ? (
                  <button
                    onClick={() => publishCorrection(s.id)}
                    data-testid={`intake-publish-correction-${s.reference}`}
                    className="mono border border-ochre bg-ochre px-3.5 py-2 text-[0.55rem] uppercase tracking-[0.14em] text-black hover:bg-white"
                  >
                    Publish to corrections log
                  </button>
                ) : null}
                {DECISIONS.map(([value, l]) => (
                  <button
                    key={value}
                    onClick={() => decide(s.id, value)}
                    data-testid={`intake-set-${value}-${s.reference}`}
                    className={`mono border px-3.5 py-2 text-[0.55rem] uppercase tracking-[0.14em] transition-colors ${
                      s.status === value ? "border-ochre bg-ochre text-black" : "border-zinc-800 text-zinc-400 hover:border-ochre hover:text-ochre"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
              {s.reviewed_by ? (
                <p className="mono mt-3 text-[0.55rem] uppercase tracking-[0.16em] text-zinc-600">
                  Last decision by {s.reviewed_by} on {new Date(s.reviewed_at).toLocaleString()}
                </p>
              ) : null}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
